import { useState, useRef, useEffect, useMemo } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getExpandedRowModel,
  flexRender,
  SortingState,
  RowSelectionState,
  VisibilityState,
  ExpandedState,
} from '@tanstack/react-table';
import {
  ChevronUp,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  ChevronsLeft,
  ChevronsRight,
  RefreshCw,
} from 'lucide-react';
import { Button } from '@/core/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/core/components/ui/select';
import { KioskSessionGridRow, HierarchicalSessionRow, LocationGroupRow } from '../types/kiosk-session-types';
import { kioskSessionsColumns, defaultSorting } from './GridColumnDefinitions';
import { GridLoadingState } from './GridLoadingState';
import { GridEmptyState } from './GridEmptyState';
import { GridErrorState } from './GridErrorState';
import { exportKioskSessionsToExcel, exportKioskSessionsToExcelGrouped } from '@/core/utils/excelExport';
import { cn } from '@/core/lib/utils';
import { GRID_STYLES } from '@/core/components/ui/grid-styles';

interface KioskSessionsGridProps {
  data: KioskSessionGridRow[] | HierarchicalSessionRow[];
  groupedSessions?: Map<string, KioskSessionGridRow[]>;
  groupByLocation?: boolean;
  isLoading: boolean;
  isRefreshing?: boolean; // Subtle loader for pagination
  error?: string;
  onRowSelect?: (session: KioskSessionGridRow) => void;
  selectedSessionId?: string;
  // Server-side pagination props (matching sessions-report)
  totalCount?: number;
  currentPage: number;
  pageSize: number;
  pageSizeOptions: number[];
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  hasMorePages?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
  // Column visibility - managed by parent
  columnVisibility: VisibilityState;
  onColumnVisibilityChange: (visibility: VisibilityState) => void;
  // Export handler
  onExport: (visibility: VisibilityState) => void;
}

/**
 * Main grid component for displaying kiosk sessions
 * Uses TanStack Table for sorting, pagination, and row selection
 */
export const KioskSessionsGrid = ({
  data,
  groupedSessions,
  groupByLocation = false,
  isLoading,
  isRefreshing = false,
  error,
  onRowSelect,
  selectedSessionId,
  totalCount = 0,
  currentPage,
  pageSize,
  pageSizeOptions,
  onPageChange,
  onPageSizeChange,
  hasMorePages = false,
  isLoadingMore = false,
  onLoadMore,
  columnVisibility,
  onColumnVisibilityChange,
  onExport
}: KioskSessionsGridProps) => {
  const [sorting, setSorting] = useState<SortingState>(defaultSorting);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const [previousDataLength, setPreviousDataLength] = useState(0);
  const savedScrollPositionRef = useRef<number>(0);

  const fallbackCurrency = useMemo(() => {
    const flat: KioskSessionGridRow[] = (data as any[]).flatMap(row =>
      row.type === 'location' ? (row.subRows ?? []) : [row]
    );
    const counts = new Map<string, number>();
    for (const s of flat) {
      const c = s.currency;
      if (c && /^[A-Z]{3}$/.test(c)) {
        try {
          new Intl.NumberFormat('en', { style: 'currency', currency: c });
          counts.set(c, (counts.get(c) ?? 0) + 1);
        } catch { /* invalid */ }
      }
    }
    if (counts.size === 0) return undefined;
    return [...counts.entries()].reduce((a, b) => (b[1] > a[1] ? b : a))[0];
  }, [data]);

  // Preserve scroll position when loading more data
  useEffect(() => {
    // Only restore scroll when data is added (pagination)
    if (data.length > previousDataLength && previousDataLength > 0) {
      // Restore the saved scroll position
      window.scrollTo(0, savedScrollPositionRef.current);
    }
    setPreviousDataLength(data.length);
  }, [data.length, previousDataLength]);

  // Helper to check if a row is a location group row
  const isLocationGroupRow = (row: any): row is LocationGroupRow => {
    return row && row.type === 'location';
  };

  // Helper to extract session from hierarchical row
  const getSessionFromRow = (row: any): KioskSessionGridRow | null => {
    if (isLocationGroupRow(row)) {
      return null;
    }
    return row as KioskSessionGridRow;
  };

  // Pagination calculations (matching sessions-report)
  const totalPages = Math.ceil(totalCount / pageSize);
  const startItem = currentPage * pageSize + 1;
  const endItem = Math.min((currentPage + 1) * pageSize, totalCount);

  // Generate page numbers to show (matching sessions-report: shows up to 4 pages)
  const getPageNumbers = () => {
    const pages: number[] = [];
    const maxPagesToShow = 4;

    let startPage = Math.max(0, currentPage - 1);
    let endPage = Math.min(totalPages - 1, startPage + maxPagesToShow - 1);

    // Adjust start if we're near the end
    if (endPage - startPage < maxPagesToShow - 1) {
      startPage = Math.max(0, endPage - maxPagesToShow + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;
  };

  const table = useReactTable({
    data,
    columns: kioskSessionsColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
    onColumnVisibilityChange: onColumnVisibilityChange,
    onExpandedChange: setExpanded,
    getSubRows: (row: any) => {
      // Return subRows for location group rows
      if (isLocationGroupRow(row)) {
        return row.subRows;
      }
      return undefined;
    },
    getRowId: (row: any) => {
      // Use different ID strategy for location vs session rows
      if (isLocationGroupRow(row)) {
        return row.id;
      }
      return (row as KioskSessionGridRow).session_id;
    },
    state: {
      sorting,
      rowSelection,
      columnVisibility,
      expanded,
    },
    enableRowSelection: (row) => {
      // Only allow selection for session rows, not location group rows
      return !isLocationGroupRow(row.original);
    },
    meta: { fallbackCurrency },
  });


  const handleLoadMore = () => {
    if (onLoadMore) {
      // Save current scroll position before loading more
      savedScrollPositionRef.current = window.scrollY;
      onLoadMore();
    }
  };

  // Handle loading state
  if (isLoading) {
    return (
      <div className="flex flex-col h-full min-h-0 overflow-hidden">
        <GridLoadingState rowCount={10} columnCount={8} />
      </div>
    );
  }

  // Handle error state
  if (error) {
    return (
      <GridErrorState
        error={error}
        title="Failed to Load Sessions"
      />
    );
  }

  // Handle empty state
  if (!data || data.length === 0) {
    return (
      <GridEmptyState
        title="No Sessions Found"
        description="No kiosk sessions found for the selected date range. Try adjusting your filters or check back later."
      />
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0 overflow-hidden">
      {/* Table container - fills available space, scrolls internally */}
      <div
        className={cn('border-x border-t bg-white flex-1 min-h-0 sticky-scrollbar', GRID_STYLES.border, isLoading && 'opacity-50')}
        style={{ overflow: 'scroll' }}
        data-testid="kiosk-sessions-grid"
      >
        <table className="w-full min-w-max table-auto caption-bottom text-sm border-separate border-spacing-0">
          <thead className={cn('sticky top-0 z-10 [&_tr]:border-b', GRID_STYLES.header.background)}>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className={cn(GRID_STYLES.header.background, 'border-b transition-colors')}>
                 {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className={cn(
                      'h-12 px-4 text-left align-middle text-xs font-medium whitespace-nowrap',
                      GRID_STYLES.header.text,
                      (header.column.columnDef.meta as any)?.align === 'right' ? 'text-right' :
                      (header.column.columnDef.meta as any)?.align === 'center' ? 'text-center' : ''
                    )}
                    style={{
                      width: header.getSize(),
                      minWidth: header.getSize(),
                    }}
                  >
                    {header.isPlaceholder ? null : (
                      <div
                        className={cn(
                          'flex items-center space-x-1',
                          header.column.getCanSort() && `cursor-pointer select-none ${GRID_STYLES.header.hoverText}`,
                          (header.column.columnDef.meta as any)?.align === 'center' ? 'justify-center' :
                          (header.column.columnDef.meta as any)?.align === 'right' ? 'justify-end' : ''
                        )}
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        <span>
                          {flexRender(header.column.columnDef.header, header.getContext())}
                        </span>
                        {header.column.getCanSort() && (
                          <div className="flex flex-col">
                            <ChevronUp className={cn('h-3 w-3', header.column.getIsSorted() === 'asc' ? GRID_STYLES.sort.active : GRID_STYLES.sort.inactive)} />
                            <ChevronDown className={cn('h-3 w-3 -mt-1', header.column.getIsSorted() === 'desc' ? GRID_STYLES.sort.active : GRID_STYLES.sort.inactive)} />
                          </div>
                        )}
                      </div>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>


          <tbody className="[&_tr:last-child]:border-0">
            {table.getRowModel().rows.map((row) => {
              const isLocationRow = isLocationGroupRow(row.original);
              const sessionData = getSessionFromRow(row.original);
              const isSelected = sessionData?.session_id === selectedSessionId;

              // PARENT ROW: Location header row (grouped view)
              if (isLocationRow) {
                const locationRow = row.original as LocationGroupRow;

                return (
                  <>
                    {/* Location Header Row */}
                    <tr
                      key={row.id}
                      data-testid="location-row"
                      className={cn('cursor-pointer font-semibold border-b transition-colors', GRID_STYLES.row.group, GRID_STYLES.row.groupHover, GRID_STYLES.border)}
                      onClick={() => row.toggleExpanded()}
                    >
                      {row.getVisibleCells().map((cell, cellIndex) => {
                        const colId = cell.column.id;

                        // First cell: location name with expand/collapse
                        if (cellIndex === 0) {
                          return (
                            <td key={cell.id} className="p-4 align-middle py-2 whitespace-nowrap">
                              <div className="flex items-center space-x-2">
                                {row.getIsExpanded() ? (
                                  <ChevronDown className="h-4 w-4" />
                                ) : (
                                  <ChevronRight className="h-4 w-4" />
                                )}
                                <span>{locationRow.locationName}</span>
                                <span className="text-sm text-gray-500">
                                  ({locationRow.sessionCount} sessions)
                                </span>
                              </div>
                            </td>
                          );
                        }

                        // Total Energy column
                        if (colId === 'total_energy') {
                          return (
                            <td key={cell.id} className="p-4 align-middle py-2 text-right whitespace-nowrap">
                              {locationRow.totalEnergy.toFixed(2)} kWh
                            </td>
                          );
                        }

                        // Settlement Amount column
                        if (colId === 'settlement_amount') {
                          return (
                            <td key={cell.id} className="p-4 align-middle py-2 text-right whitespace-nowrap">
                              {locationRow.totalSettlement.toFixed(2)}
                            </td>
                          );
                        }

                        // All other cells: empty
                        return (
                          <td key={cell.id} className="p-4 align-middle py-2 whitespace-nowrap" />
                        );
                      })}
                    </tr>

                    {/* CHILD ROWS: Session data (only when expanded) - renders as normal grid rows */}
                    {row.getIsExpanded() && row.subRows?.map((subRow) => {
                      const subSessionData = getSessionFromRow(subRow.original);
                      const isSubSelected = subSessionData?.session_id === selectedSessionId;

                      return (
                        <tr
                          key={subRow.id}
                          data-testid="session-row"
                          data-state={subRow.getIsSelected() ? "selected" : undefined}
                          className={cn(
                            'cursor-pointer border-b transition-colors',
                            GRID_STYLES.border,
                            isSubSelected
                              ? `${GRID_STYLES.row.selected} ${GRID_STYLES.row.selectedHover}`
                              : ((subSessionData?.settAmount == null || subSessionData?.settAmount <= 0) && subSessionData?.total_energy > 0)
                                ? 'bg-yellow-50 hover:bg-yellow-100'
                                : `${GRID_STYLES.row.default} ${GRID_STYLES.row.hover}`
                          )}
                          onDoubleClick={() => {
                            subRow.toggleSelected();
                            if (subSessionData) {
                              onRowSelect?.(subSessionData);
                            }
                          }}
                        >
                          {/* Render all cells normally like flat view */}
                          {subRow.getVisibleCells().map((cell, cellIndex) => (
                            <td
                              key={cell.id}
                              className={`p-4 align-middle py-2 whitespace-nowrap ${
                                (cell.column.columnDef.meta as any)?.align === 'right' ? 'text-right' :
                                (cell.column.columnDef.meta as any)?.align === 'center' ? 'text-center' : ''
                              } ${cellIndex === 0 ? 'pl-8' : ''}`}
                            >
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </>
                );
              }

              // FLAT VIEW: Regular session row (no grouping)
              return (
                <tr
                  key={row.id}
                  data-state={row.getIsSelected() ? "selected" : undefined}
                  data-testid="session-row"
                  className={cn(
                    'cursor-pointer border-b transition-colors',
                    GRID_STYLES.border,
                    isSelected
                      ? `${GRID_STYLES.row.selected} ${GRID_STYLES.row.selectedHover}`
                      : ((sessionData?.settAmount == null || sessionData?.settAmount <= 0) && sessionData?.total_energy > 0)
                        ? 'bg-yellow-50 hover:bg-yellow-100'
                        : `${GRID_STYLES.row.default} ${GRID_STYLES.row.hover}`
                  )}
                  onDoubleClick={() => {
                    row.toggleSelected();
                    if (sessionData) {
                      onRowSelect?.(sessionData);
                    }
                  }}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className={`p-4 align-middle py-2 whitespace-nowrap ${
                        (cell.column.columnDef.meta as any)?.align === 'right' ? 'text-right' :
                        (cell.column.columnDef.meta as any)?.align === 'center' ? 'text-center' : ''
                      }`}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination Footer - Always visible at bottom (matching sessions-report) */}
      <div className={cn('flex-shrink-0 flex items-center justify-between px-2 py-3 border-x border-b bg-white', GRID_STYLES.border)}>
        {/* Page Navigation */}
        <div className="flex items-center gap-1">
          {/* First Page */}
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0 text-[#262626] hover:bg-[#FFFBEB]"
            onClick={() => onPageChange(0)}
            disabled={currentPage === 0 || isLoading || isRefreshing}
          >
            <ChevronsLeft className="h-4 w-4" />
          </Button>

          {/* Previous Page */}
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0 text-[#262626] hover:bg-[#FFFBEB]"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 0 || isLoading || isRefreshing}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>

          {/* Page Numbers */}
          {getPageNumbers().map((pageNum) => (
            <Button
              key={pageNum}
              variant={currentPage === pageNum ? 'default' : 'ghost'}
              size="sm"
              className={`h-8 w-8 p-0 ${
                currentPage === pageNum
                  ? 'bg-[#FFCD00] hover:bg-[#E6B800] text-[#262626]'
                  : 'text-[#262626] hover:bg-[#FFFBEB]'
              }`}
              onClick={() => onPageChange(pageNum)}
              disabled={isLoading || isRefreshing}
            >
              {pageNum + 1}
            </Button>
          ))}

          {/* Next Page */}
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0 text-[#262626] hover:bg-[#FFFBEB]"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage >= totalPages - 1 || isLoading || isRefreshing}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>

          {/* Last Page */}
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0 text-[#262626] hover:bg-[#FFFBEB]"
            onClick={() => onPageChange(totalPages - 1)}
            disabled={currentPage >= totalPages - 1 || isLoading || isRefreshing}
          >
            <ChevronsRight className="h-4 w-4" />
          </Button>

          {/* Page Info */}
          <span className="text-sm text-[#606060] ml-2">
            {currentPage + 1} of {totalPages} pages
          </span>
        </div>

        {/* Right side - showing range and page size */}
        <div className="flex items-center gap-4">
          {/* Range Info */}
          <span className="text-sm text-[#606060]">
            {startItem}-{endItem} of {totalCount.toLocaleString()}
          </span>

          {/* Rows per page */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-[#606060]">Rows per page:</span>
            <Select
              value={pageSize.toString()}
              onValueChange={(value) => onPageSizeChange(parseInt(value, 10))}
              disabled={isLoading || isRefreshing}
            >
              <SelectTrigger className="h-8 w-[70px] border-[#E8E8E8] text-[#262626]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {pageSizeOptions.map((size) => (
                  <SelectItem key={size} value={size.toString()} className="hover:bg-[#FFFBEB]">
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
    </div>
  );
};
